import {
  CatalogAccessChanges,
  CatalogAccessEntityDTO,
  CatalogAccessFieldChanges,
} from '../../../../../../../../services/modules/catalogs/types/catalogAccess.types';
import { toDateOrNull } from '../../../../../../../../utils/dates';
import { isStartDateBeforeEndDate } from '../../../../../../../../utils/schemaUtils/validateIsStartDateBeforeEndDate.utils';
import { CATALOG_CREATION_ACCESS_TABS } from '../../../../../../creation/catalogCreationAccess.consts';
import { CatalogCreationAccessRow } from '../../../../../../creation/catalogCreationAccess.types';
import {
  AddCatalogAccessDraftEntitiesParams,
  BuildCatalogAccessRowsParams,
  CatalogAccessDates,
  CatalogAccessDraftByField,
  CatalogAccessFieldDraft,
  ChangeCatalogAccessDraftDateParams,
  ChangeCatalogAccessDraftEntitiesDatesParams,
  CountCatalogAccessDraftEntitiesParams,
} from './catalogAccessSection.types';

export const EMPTY_CATALOG_ACCESS_FIELD_DRAFT: CatalogAccessFieldDraft = {
  addedById: {},
  datesById: {},
  removedIds: [],
  isRemovingAll: false,
  allDates: null,
};

export const EMPTY_CATALOG_ACCESS_DRAFT: CatalogAccessDraftByField = {
  classificationPermissions: EMPTY_CATALOG_ACCESS_FIELD_DRAFT,
  customers: EMPTY_CATALOG_ACCESS_FIELD_DRAFT,
  salesRepresentatives: EMPTY_CATALOG_ACCESS_FIELD_DRAFT,
};

export const resolveCatalogAccessEntityName = (
  entity: CatalogAccessEntityDTO,
  nameField: 'name' | 'commercialName',
) => entity[nameField] ?? entity.id;

export const buildCatalogAccessRows = ({
  entities,
  nameField,
  draft,
}: BuildCatalogAccessRowsParams): CatalogCreationAccessRow[] => {
  const removedIds = new Set(draft.removedIds);

  const addedRows = Object.entries(draft.addedById).map(([entityId, added]) => ({
    id: entityId,
    name: added.name,
    startDate: added.startDate,
    endDate: added.endDate,
  }));

  const serverRows = draft.isRemovingAll
    ? []
    : entities
        .filter((entity) => !removedIds.has(entity.id) && !draft.addedById[entity.id])
        .map((entity) => ({
          id: entity.id,
          name: resolveCatalogAccessEntityName(entity, nameField),
          startDate: draft.datesById[entity.id]?.startDate ?? toDateOrNull(entity.startDate),
          endDate: draft.datesById[entity.id]?.endDate ?? toDateOrNull(entity.endDate),
        }));

  const rows = [...addedRows, ...serverRows];

  if (!draft.allDates) {
    return rows;
  }

  return rows.map((row) => ({ ...row, ...draft.allDates }));
};

export const countCatalogAccessDraftEntities = ({
  draft,
  serverCount,
}: CountCatalogAccessDraftEntitiesParams) => {
  const remainingServerCount = draft.isRemovingAll ? 0 : serverCount - draft.removedIds.length;

  return remainingServerCount + Object.keys(draft.addedById).length;
};

export const addCatalogAccessDraftEntities = ({
  draft,
  entityIds,
  entityNames,
  startDate,
  endDate,
}: AddCatalogAccessDraftEntitiesParams): CatalogAccessFieldDraft => {
  const addedIds = new Set(entityIds);

  const addedById = { ...draft.addedById };

  entityIds.forEach((entityId) => {
    addedById[entityId] = { name: entityNames[entityId] ?? entityId, startDate, endDate };
  });

  return {
    ...draft,
    addedById,
    removedIds: draft.removedIds.filter((removedId) => !addedIds.has(removedId)),
  };
};

export const removeCatalogAccessDraftEntity = (
  draft: CatalogAccessFieldDraft,
  entityId: string,
): CatalogAccessFieldDraft => {
  if (draft.addedById[entityId]) {
    const { [entityId]: _removedEntity, ...addedById } = draft.addedById;

    return { ...draft, addedById };
  }

  const { [entityId]: _removedDates, ...datesById } = draft.datesById;

  return { ...draft, datesById, removedIds: [...draft.removedIds, entityId] };
};

export const removeCatalogAccessDraftEntities = (
  draft: CatalogAccessFieldDraft,
  entityIds: string[],
): CatalogAccessFieldDraft => entityIds.reduce(removeCatalogAccessDraftEntity, draft);

export const REMOVING_ALL_CATALOG_ACCESS_FIELD_DRAFT: CatalogAccessFieldDraft = {
  ...EMPTY_CATALOG_ACCESS_FIELD_DRAFT,
  isRemovingAll: true,
};

export const changeCatalogAccessDraftEntitiesDates = ({
  draft,
  entityIds,
  dates,
}: ChangeCatalogAccessDraftEntitiesDatesParams): CatalogAccessFieldDraft => {
  const addedById = { ...draft.addedById };
  const datesById = { ...draft.datesById };

  entityIds.forEach((entityId) => {
    const added = addedById[entityId];

    if (added) {
      addedById[entityId] = { ...added, ...dates };

      return;
    }

    datesById[entityId] = { ...dates };
  });

  return { ...draft, addedById, datesById };
};

export const changeAllCatalogAccessDraftDates = (
  draft: CatalogAccessFieldDraft,
  dates: CatalogAccessDates,
): CatalogAccessFieldDraft => ({
  ...draft,
  addedById: Object.fromEntries(
    Object.entries(draft.addedById).map(([entityId, added]) => [entityId, { ...added, ...dates }]),
  ),
  datesById: {},
  allDates: dates,
});

export const changeCatalogAccessDraftDate = ({
  draft,
  entityId,
  dateField,
  date,
  currentDates,
}: ChangeCatalogAccessDraftDateParams): CatalogAccessFieldDraft => {
  const added = draft.addedById[entityId];

  if (added) {
    return {
      ...draft,
      addedById: { ...draft.addedById, [entityId]: { ...added, [dateField]: date } },
    };
  }

  return {
    ...draft,
    datesById: {
      ...draft.datesById,
      [entityId]: { ...currentDates, [dateField]: date },
    },
  };
};

export const hasInvalidCatalogAccessDraftDates = (draftByField: CatalogAccessDraftByField) =>
  Object.values(draftByField).some((draft) =>
    [
      ...Object.values(draft.addedById),
      ...Object.values(draft.datesById),
      ...(draft.allDates ? [draft.allDates] : []),
    ].some((dates) => !isStartDateBeforeEndDate(dates)),
  );

export const hasCatalogAccessDraftChanges = (draftByField: CatalogAccessDraftByField) =>
  Object.values(draftByField).some(
    (draft) =>
      draft.isRemovingAll ||
      draft.allDates !== null ||
      draft.removedIds.length > 0 ||
      Object.keys(draft.addedById).length > 0 ||
      Object.keys(draft.datesById).length > 0,
  );

const buildFieldChanges = (draft: CatalogAccessFieldDraft): CatalogAccessFieldChanges => ({
  added: Object.entries(draft.addedById).map(([entityId, added]) => ({
    entityId,
    startDate: added.startDate,
    endDate: added.endDate,
  })),
  updated: Object.entries(draft.datesById).map(([entityId, dates]) => ({
    entityId,
    startDate: dates.startDate,
    endDate: dates.endDate,
  })),
  removedIds: draft.removedIds,
  isRemovingAll: draft.isRemovingAll,
  allDates: draft.allDates,
});

export const buildCatalogAccessChanges = (
  draftByField: CatalogAccessDraftByField,
): CatalogAccessChanges =>
  CATALOG_CREATION_ACCESS_TABS.reduce((changes, { field }) => {
    changes[field] = buildFieldChanges(draftByField[field]);

    return changes;
  }, {} as CatalogAccessChanges);
